import type { ThemePropertyType } from '@repo/lib';
import { z } from '../zod';

export const THEME_PROPERTY_GROUPS = [
  'tokens',
  'layout',
  'labels',
  'elements',
  'customCss',
] as const;

// An object rather than an array so the compiler flags a registry type missing here.
const THEME_PROPERTY_TYPES = Object.keys({
  color: true,
  font: true,
  fontWeight: true,
  fontStyle: true,
  fontSize: true,
  size: true,
  shadow: true,
  alignment: true,
  select: true,
  toggle: true,
  image: true,
  text: true,
} satisfies Record<ThemePropertyType, true>) as [ThemePropertyType, ...ThemePropertyType[]];

export const ListThemePropertiesInputValidationSchema = z.object({
  group: z
    .enum(THEME_PROPERTY_GROUPS)
    .optional()
    .meta({
      description:
        'Only list one group. `tokens`: brand colors, fonts and corner radius used across the page. `layout`: backgrounds, images and column settings of each hosted layout. ' +
        '`labels`: the text customers see, which is also where translations go. `elements`: styling of single parts of the page, such as the title, price options, variants, form fields, pay button and trust badges. ' +
        '`customCss`: CSS added to the page, for changes the other properties cannot make.',
    }),
  prefix: z
    .string()
    .max(200)
    .regex(/^[A-Za-z][A-Za-z0-9]*(\.[A-Za-z][A-Za-z0-9]*)*$/, {
      message: 'Must be a property path such as elements.checkout.form.button',
    })
    .refine(prefix => prefix !== 'variables' && !prefix.startsWith('variables.'), {
      message: 'Leave out variables.: paths start at tokens, layout, labels, elements or customCss',
    })
    .optional()
    .meta({
      description:
        'Only list the properties under this path, matched on whole segments, for example `elements.product.pricePicker` or `elements.checkout.form.button`. ' +
        'The cheapest way to read the properties of one part of the page.',
    }),
  search: z
    .string()
    .min(1)
    .max(100)
    .optional()
    .meta({
      description:
        'One or two words for what you are changing, for example `button radius`, `pay button color` or `heading font`, or a path or key such as `colorPrimary`. ' +
        'Each word matches the start of a word in the property path or description, case-insensitively, so `radi` finds `borderRadius`. ' +
        'Common words such as `corner`, `rounded`, `pay button` and `colour` find the properties they mean. ' +
        'Lists the properties that match every word or, when none do, those that match any word, best match first, and at most 40 of them. ' +
        'Words such as `the`, `on` or `make` are ignored.',
    }),
});

export const ThemePropertyToolDataValidationSchema = z.object({
  path: z.string().meta({
    description:
      'Where the value goes under `themeOverrides`, one key per dot: `tokens.colorPrimary` is `{ "tokens": { "colorPrimary": <value> } }`.',
  }),
  type: z.enum(THEME_PROPERTY_TYPES).meta({
    description: 'The value format. The tool description lists the format of each type.',
  }),
  description: z.string().meta({ description: 'What the property changes on the page.' }),
  default: z.unknown().optional().meta({
    description:
      "The built-in default, when the property has a fixed one: every label has its default text here. A property without one follows the page's theme.",
  }),
  options: z
    .array(z.union([z.string(), z.number()]))
    .optional()
    .meta({
      description:
        'The only values the property accepts, when it takes one of a list. `""` means Auto.',
    }),
});

export const ThemePropertiesToolResponseValidationSchema = z.object({
  data: z.array(ThemePropertyToolDataValidationSchema).meta({
    description: 'The theme properties the page tools accept under `themeOverrides`.',
  }),
  total: z.number().int().meta({
    description:
      'Number of properties that match. A search returns at most the best 40, so `total` can be more than the rows in `data`.',
  }),
  note: z
    .string()
    .optional()
    .meta({
      description:
        'Set when a search was cut to its best matches, matched only some of its words, or had a word nothing matches: what to change in the next call. ' +
        'When `customCss` is listed, it also says which class names custom CSS can select and how to write it.',
    }),
});

export type ListThemePropertiesInput = z.infer<typeof ListThemePropertiesInputValidationSchema>;
export type ThemePropertiesToolResponse = z.infer<
  typeof ThemePropertiesToolResponseValidationSchema
>;
